// src/pages/AcopMonitor.jsx
//
// Interactive ACOP Co-Editor.
//
// LEFT  — sidebar: decisions in document order, only "changes" shown
//         (critical/needs_input/auto_applied/resolved). Click expands
//         inline with full resolution UI (option buttons, custom input).
// RIGHT — the actual ACOP document. Where each decision applies, the
//         exact flagged_phrase is highlighted with the decision's color.
//         Insertions get inline marker cards at insertion_point.
//
// Picking an option in the sidebar:
//   - swaps the highlighted text in the doc for the option's replacement_text
//   - flips the color from yellow/red → green
//   - persists via /resolve
//
// Custom inputs:
//   - For single_select: free-form textarea (no value mode)
//   - For number_range: number input constrained by input_config (min, max,
//     value_type) — typed into {{value}} placeholder in the template

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle, AlertCircle, CheckCircle2, Info,
  Search, RefreshCw, FileText, ChevronDown, ChevronUp,
  RotateCcw, Sparkles
} from 'lucide-react';

const COMPLIANCE_API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:3200'
  : 'https://housing-compliance-service.onrender.com';

// ─── Status visual config ──
const STATUS = {
  needs_input: {
    label: 'Needs Input', tag: 'DECISION NEEDED',
    dot: 'bg-yellow-500', text: 'text-yellow-800',
    bg: 'bg-yellow-50', border: 'border-yellow-300', leftBorder: 'border-l-yellow-500',
    highlight: 'bg-yellow-200 underline decoration-yellow-600 decoration-2',
    cardBg: 'bg-yellow-50', cardBorder: 'border-l-4 border-yellow-500',
    icon: AlertCircle,
  },
  auto_applied: {
    label: 'Auto-Applied', tag: 'AUTO-UPDATED',
    dot: 'bg-blue-500', text: 'text-blue-800',
    bg: 'bg-blue-50', border: 'border-blue-300', leftBorder: 'border-l-blue-500',
    highlight: 'bg-blue-100 underline decoration-blue-400 decoration-2',
    cardBg: 'bg-blue-50', cardBorder: 'border-l-4 border-blue-500',
    icon: Info,
  },
  current: {
    label: 'Resolved', tag: 'RESOLVED',
    dot: 'bg-green-500', text: 'text-green-800',
    bg: 'bg-green-50', border: 'border-green-300', leftBorder: 'border-l-green-500',
    highlight: 'bg-green-100 underline decoration-green-500 decoration-2',
    cardBg: 'bg-green-50', cardBorder: 'border-l-4 border-green-500',
    icon: CheckCircle2,
  },
};

const FILTERS = ['all', 'needs_input', 'auto_applied', 'current'];

// ─── Fuzzy match: ignore whitespace + case for highlight finding ──
function fuzzyFind(haystack, needle) {
  if (!haystack || !needle || needle.length < 5) return null;
  const indexMap = [];
  const normParts = [];
  let lastSpace = false;
  for (let i = 0; i < haystack.length; i++) {
    const c = haystack[i];
    if (/\s/.test(c)) {
      if (!lastSpace && normParts.length > 0) {
        normParts.push(' ');
        indexMap.push(i);
      }
      lastSpace = true;
    } else {
      normParts.push(c.toLowerCase());
      indexMap.push(i);
      lastSpace = false;
    }
  }
  const stripQuotes = (str) => str
    .replace(/[\u2018\u2019\u201B\u2032]/g, "'")   // curly/prime single quotes -> '
    .replace(/[\u201C\u201D\u201F\u2033]/g, '"')   // curly/prime double quotes -> "
    .replace(/[\u2013\u2014]/g, '-');              // en/em dash -> hyphen
  const normH = stripQuotes(normParts.join('')).trim();
  const normN = stripQuotes(needle.replace(/\s+/g, ' ').toLowerCase()).trim();
  if (!normN) return null;
  const idx = normH.indexOf(normN);
  if (idx < 0) return null;
  const start = indexMap[idx];
  const end = indexMap[Math.min(idx + normN.length - 1, indexMap.length - 1)] + 1;
  return { start, end };
}

// ─── Build text chunks for a section with embedded highlights ──
function buildSectionChunks(section, decisions, expandedId) {
  if (!section.text) return { chunks: [], replacements: [], insertions: [], unmatched: [] };

  // Replacements with successful flagged_phrase match
  const matchedReps = [];
  const unmatchedReps = [];
  for (const d of decisions) {
    // replacements AND deletions both anchor on the verbatim flagged_phrase
    if (d.decisionType !== 'replacement' && d.decisionType !== 'deletion') continue;
    const needle = d.flaggedPhrase;
    if (!needle) { unmatchedReps.push(d); continue; }
    const found = fuzzyFind(section.text, needle);
    if (found) {
      matchedReps.push({ ...found, decision: d });
    } else {
      unmatchedReps.push(d);
    }
  }

  // Insertions for this section (anchored by after_quote or after_heading)
  const insertions = decisions
    .filter(d => d.decisionType === 'insertion')
    .map(d => {
      const ip = d.insertionPoint || {};
      let pos = null;
      if (ip.after_quote) {
        const found = fuzzyFind(section.text, ip.after_quote);
        if (found) pos = found.end;
      }
      if (pos === null && ip.after_heading && section.heading) {
        if (section.heading.toLowerCase().includes(ip.after_heading.toLowerCase().slice(0, 40))) {
          pos = 0; // insert at top of section
        }
      }
      return { decision: d, pos };
    });

  const placedInsertions = insertions.filter(i => i.pos !== null);
  const unplacedInsertions = insertions.filter(i => i.pos === null);

  // Sort + dedupe overlapping replacements
  matchedReps.sort((a, b) => a.start - b.start);
  const dedup = [];
  let lastEnd = -1;
  for (const r of matchedReps) {
    if (r.start >= lastEnd) {
      dedup.push(r);
      lastEnd = r.end;
    }
  }

  // Merge replacements + placed insertions into a single sorted span list
  const spans = [
    ...dedup.map(r => ({ type: 'replacement', start: r.start, end: r.end, decision: r.decision })),
    ...placedInsertions.map(i => ({ type: 'insertion', start: i.pos, end: i.pos, decision: i.decision }))
  ].sort((a, b) => a.start - b.start || (a.type === 'replacement' ? -1 : 1));

  // Build chunks
  const chunks = [];
  let pos = 0;
  for (const span of spans) {
    if (span.start > pos) chunks.push({ type: 'text', text: section.text.slice(pos, span.start) });
    if (span.type === 'replacement') {
      chunks.push({
        type: 'replacement',
        text: section.text.slice(span.start, span.end),
        decision: span.decision
      });
      pos = span.end;
    } else {
      chunks.push({ type: 'insertion', decision: span.decision });
      // insertion is a marker, doesn't consume text
    }
  }
  if (pos < section.text.length) chunks.push({ type: 'text', text: section.text.slice(pos) });

  return {
    chunks,
    unmatched: [...unmatchedReps, ...unplacedInsertions.map(i => i.decision)]
  };
}

// ─── Format a custom value into number_range template ──
function applyValueToTemplate(template, value) {
  if (!template) return '';
  return template.replace(/\{\{\s*value\s*\}\}/g, value);
}

// ─── Validate custom number_range input ──
function validateNumberInput(value, config) {
  config = config || {};
  if (value === '' || value == null) return { valid: false, error: 'Required' };
  const n = Number(value);
  if (Number.isNaN(n)) return { valid: false, error: 'Must be a number' };
  const min = config.min != null ? config.min : 0;            // never allow negatives unless reg sets a negative floor
  if (n < min) return { valid: false, error: `Must be ≥ ${min}` };
  if (config.max != null && n > config.max) return { valid: false, error: `Must be ≤ ${config.max}` };
  const intLike = ['integer', 'days', 'months', 'years'].includes(config.value_type);
  if (intLike && !Number.isInteger(n)) return { valid: false, error: 'Must be a whole number' };
  return { valid: true };
}

// ═══════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════
const AcopMonitor = ({ currentUser }) => {
  const [decisionsData, setDecisionsData] = useState(null);
  const [docData, setDocData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingResolve, setPendingResolve] = useState({});

  const sidebarRefs = useRef({});
  const docMarkRefs = useRef({});

  useEffect(() => {
    if (currentUser) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const loadAll = async () => {
    setLoading(true); setError('');
    try {
      const [decRes, docRes] = await Promise.all([
        fetch(`${COMPLIANCE_API_URL}/api/acop-monitor/decisions/${currentUser}`),
        fetch(`${COMPLIANCE_API_URL}/api/acop-monitor/document/${currentUser}`),
      ]);
      if (!decRes.ok) throw new Error(`decisions ${decRes.status}`);
      if (!docRes.ok) throw new Error(`document ${docRes.status}`);
      setDecisionsData(await decRes.json());
      setDocData(await docRes.json());
    } catch (err) {
      console.error('AcopMonitor load:', err);
      setError('Failed to load compliance audit.');
    } finally {
      setLoading(false);
    }
  };

  // Decisions by section
  const decisionsBySection = useMemo(() => {
    const map = {};
    (decisionsData?.decisions || []).forEach(d => {
      if (d.sectionId) {
        if (!map[d.sectionId]) map[d.sectionId] = [];
        map[d.sectionId].push(d);
      }
    });
    return map;
  }, [decisionsData]);

  // Gap decisions
  const gapDecisions = useMemo(() => {
    return (decisionsData?.decisions || []).filter(d => !d.sectionId);
  }, [decisionsData]);

  // Filtered for sidebar — note: 'current' items shown ONLY if filter explicitly selects them
  const filteredDecisions = useMemo(() => {
    if (!decisionsData?.decisions) return [];
    let list = decisionsData.decisions;
    // Default: hide gray "current" items (audit-only, no change to show)
    if (activeFilter === 'all') {
      list = list.filter(d => d.uiStatus !== 'current' || d.wasResolvedByHuman);
    } else {
      list = list.filter(d => d.uiStatus === activeFilter);
    }
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      list = list.filter(d =>
        (d.title || '').toLowerCase().includes(s) ||
        (d.taxonomyCode || '').toLowerCase().includes(s) ||
        (d.category || '').toLowerCase().includes(s)
      );
    }
    return list;
  }, [decisionsData, activeFilter, searchTerm]);

  const patchDecision = useCallback((id, patch) => {
    setDecisionsData(prev => {
      if (!prev) return prev;
      const updated = prev.decisions.map(d => d.id === id ? { ...d, ...patch } : d);
      const counts = updated.reduce((acc, d) => {
        acc[d.uiStatus] = (acc[d.uiStatus] || 0) + 1;
        acc.total++;
        return acc;
      }, { needs_input: 0, auto_applied: 0, current: 0, total: 0 });
      return { ...prev, decisions: updated, counts };
    });
  }, []);

  const resolveDecision = async (id, { optionId, resolutionText, resolutionValue, custom }) => {
    if (!resolutionText) return;
    setPendingResolve(p => ({ ...p, [id]: true }));
    const prior = decisionsData.decisions.find(d => d.id === id);
    patchDecision(id, {
      status: 'resolved', uiStatus: 'current', color: 'green',
      resolutionChoice: optionId || null, resolutionText,
      resolutionValue: resolutionValue || null, customInput: !!custom,
      hasResolution: true, wasResolvedByHuman: true, resolvedBy: 'human',
    });
    try {
      const res = await fetch(`${COMPLIANCE_API_URL}/api/acop-monitor/decisions/${currentUser}/${id}/resolve`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ optionId, resolutionText, resolutionValue, custom: !!custom }),
      });
      if (!res.ok) throw new Error(`resolve ${res.status}`);
      const data = await res.json();
      if (data.decision) patchDecision(id, data.decision);
    } catch (err) {
      console.error('resolve error:', err);
      if (prior) patchDecision(id, prior);
      alert('Failed to save resolution.');
    } finally {
      setPendingResolve(p => ({ ...p, [id]: false }));
    }
  };

  const undoResolution = async (id) => {
    setPendingResolve(p => ({ ...p, [id]: true }));
    const prior = decisionsData.decisions.find(d => d.id === id);
    try {
      const res = await fetch(`${COMPLIANCE_API_URL}/api/acop-monitor/decisions/${currentUser}/${id}/undo`, { method: 'POST' });
      if (!res.ok) throw new Error(`undo ${res.status}`);
      const data = await res.json();
      if (data.decision) patchDecision(id, data.decision);
    } catch (err) {
      console.error('undo error:', err);
      if (prior) patchDecision(id, prior);
      alert('Failed to undo.');
    } finally {
      setPendingResolve(p => ({ ...p, [id]: false }));
    }
  };

  const selectDecision = (id) => {
    setExpandedId(prev => prev === id ? null : id);
    setTimeout(() => {
      const el = docMarkRefs.current[id];
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  const focusFromDoc = (id) => {
    setExpandedId(id);
    setTimeout(() => {
      const el = sidebarRefs.current[id];
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4" />
        <p className="text-gray-500">Loading compliance audit...</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <AlertTriangle className="h-10 w-10 text-red-500 mx-auto mb-3" />
        <p className="text-red-700 mb-4">{error}</p>
        <button onClick={loadAll} className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">Retry</button>
      </div>
    );
  }
  if (!decisionsData || decisionsData.decisions.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-12 text-center">
        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-700 mb-2">No audit available yet</h3>
        <p className="text-gray-500">Contact your HousingAI representative.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              {decisionsData.document?.name || 'ACOP'}
              {decisionsData.version && (
                <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                  Version {decisionsData.version.number}
                </span>
              )}
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {decisionsData.orgName} · {decisionsData.counts.total} items reviewed
            </p>
          </div>
          <button onClick={loadAll} className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Stat pills */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {['needs_input', 'auto_applied', 'current'].map(s => {
          const cfg = STATUS[s];
          const count = decisionsData.counts[s] || 0;
          const Icon = cfg.icon;
          const isActive = activeFilter === s;
          return (
            <button key={s}
              onClick={() => setActiveFilter(isActive ? 'all' : s)}
              className={`text-left bg-white rounded-xl p-4 shadow-sm border-2 transition-all hover:shadow-md ${isActive ? cfg.border : 'border-transparent'}`}>
              <div className="flex items-start justify-between mb-2">
                <div className={`p-2 rounded-lg ${cfg.bg}`}>
                  <Icon className={`h-5 w-5 ${cfg.text}`} />
                </div>
                <span className="text-2xl font-bold text-gray-900">{count}</span>
              </div>
              <p className={`text-sm font-medium ${cfg.text}`}>{cfg.label}</p>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
        <div className="flex gap-2 flex-wrap">
          {FILTERS.map(f => {
            const isActive = activeFilter === f;
            const cfg = f === 'all' ? null : STATUS[f];
            const label = f === 'all' ? `All Changes` : `${cfg.label} (${decisionsData.counts[f] || 0})`;
            return (
              <button key={f} onClick={() => setActiveFilter(f)}
                className={`px-3 py-1.5 text-sm rounded-full border ${
                  isActive
                    ? f === 'all' ? 'bg-gray-900 text-white border-gray-900' : `${cfg.bg} ${cfg.text} ${cfg.border}`
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}>{label}</button>
            );
          })}
        </div>
        <div className="flex-1 relative md:ml-auto md:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input type="text" placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
        </div>
      </div>

      {/* Split view */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" style={{ minHeight: '720px' }}>
        {/* Sidebar */}
        <div className="lg:col-span-4 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
          <div className="px-4 py-2 border-b border-gray-100 bg-gray-50">
            <p className="text-xs font-semibold text-gray-600 uppercase">
              {filteredDecisions.length} {filteredDecisions.length === 1 ? 'change' : 'changes'}
            </p>
          </div>
          <div className="divide-y divide-gray-100 overflow-y-auto" style={{ maxHeight: '720px' }}>
            {filteredDecisions.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">No changes match your filter.</div>
            ) : filteredDecisions.map(d => (
              <SidebarItem key={d.id} decision={d}
                expanded={expandedId === d.id}
                onSelect={() => selectDecision(d.id)}
                onResolve={(payload) => resolveDecision(d.id, payload)}
                onUndo={() => undoResolution(d.id)}
                pending={!!pendingResolve[d.id]}
                refCb={(el) => { if (el) sidebarRefs.current[d.id] = el; }} />
            ))}
          </div>
        </div>

        {/* Document */}
        <div className="lg:col-span-8 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-2 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
            <p className="text-xs font-semibold text-gray-600 uppercase">Your ACOP</p>
            <p className="text-xs text-gray-400">Click any highlight to expand its decision</p>
          </div>
          <div className="overflow-y-auto px-8 py-6" style={{ maxHeight: '720px', fontFamily: 'Georgia, "Source Serif 4", serif' }}>
            {docData?.sections?.length > 0 ? (
              <>
                {docData.sections.map(section => (
                  <DocSection key={section.id} section={section}
                    decisions={decisionsBySection[section.id] || []}
                    expandedId={expandedId}
                    onFocus={focusFromDoc}
                    markRefCb={(decId, el) => { if (el) docMarkRefs.current[decId] = el; }} />
                ))}
                {gapDecisions.length > 0 && (
                  <GapDecisions decisions={gapDecisions} expandedId={expandedId}
                    onFocus={focusFromDoc}
                    markRefCb={(decId, el) => { if (el) docMarkRefs.current[decId] = el; }} />
                )}
              </>
            ) : (
              <p className="text-gray-500 text-sm">No document text available.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// Document section
// ═══════════════════════════════════════════════════════════
const DocSection = ({ section, decisions, expandedId, onFocus, markRefCb }) => {
  const { chunks } = useMemo(
    () => buildSectionChunks(section, decisions, expandedId),
    [section, decisions, expandedId]
  );

  return (
    <div className="mb-5">
      {section.heading && (
        <h3 className="text-base font-bold text-gray-900 mt-4 mb-2">{section.heading}</h3>
      )}
      <div className="text-gray-800 text-[14px] whitespace-pre-wrap" style={{ lineHeight: 1.7 }}>
        {chunks.map((chunk, i) => {
          if (chunk.type === 'text') return <span key={i}>{chunk.text}</span>;
          if (chunk.type === 'replacement') {
            const d = chunk.decision;
            const cfg = STATUS[d.uiStatus] || STATUS.current;
            const focused = expandedId === d.id;
            const isDeletion = d.decisionType === 'deletion';
            // Blue (auto) + green (resolved) show the FINAL applied text.
            // Yellow shows the original flagged text until the PHA acts.
            const showFinal = (d.uiStatus === 'current' || d.uiStatus === 'auto_applied') && d.resolutionText;
            const displayText = (showFinal && !isDeletion) ? d.resolutionText : chunk.text;
            return (
              <mark key={i}
                ref={(el) => markRefCb(d.id, el)}
                onClick={() => onFocus(d.id)}
                className={`cursor-pointer rounded px-0.5 ${cfg.highlight} ${isDeletion ? 'line-through' : ''} ${focused ? 'ring-2 ring-offset-1 ring-blue-400' : ''}`}
                title={`${cfg.tag}${isDeletion ? ' (remove)' : ''}: ${d.title}`}>
                {displayText}
              </mark>
            );
          }
          if (chunk.type === 'insertion') {
            const d = chunk.decision;
            const cfg = STATUS[d.uiStatus] || STATUS.current;
            const focused = expandedId === d.id;
            const resolvedText = (d.uiStatus === 'current' || d.uiStatus === 'auto_applied') && d.resolutionText
              ? d.resolutionText
              : null;
            // Inline inserted text in the decision's color. Until resolved
            // (yellow), show a compact inline placeholder to fill on resolve.
            return (
              <mark key={i}
                ref={(el) => markRefCb(d.id, el)}
                onClick={() => onFocus(d.id)}
                className={`cursor-pointer rounded px-0.5 ${cfg.highlight} ${focused ? 'ring-2 ring-offset-1 ring-blue-400' : ''}`}
                title={`INSERT — ${cfg.tag}: ${d.title}`}>
                {resolvedText
                  ? resolvedText
                  : <span className="font-medium not-italic">[+ {d.title}]</span>}
              </mark>
            );
          }
          return null;
        })}
      </div>

      </div>
  );
};

// ═══════════════════════════════════════════════════════════
// Unmatched/fallback card (when flagged_phrase couldn't be located)
// ═══════════════════════════════════════════════════════════
const UnmatchedCard = ({ decision, focused, onClick, refCb }) => {
  const cfg = STATUS[decision.uiStatus] || STATUS.current;
  const Icon = cfg.icon;
  return (
    <div ref={refCb} onClick={onClick}
      className={`my-2 cursor-pointer rounded-md ${cfg.cardBg} ${cfg.cardBorder} px-3 py-2 transition-all hover:shadow-sm ${focused ? 'ring-2 ring-blue-400 shadow-md' : ''}`}
      style={{ fontFamily: 'system-ui, sans-serif' }}>
      <div className="flex items-center gap-2 mb-0.5">
        <Icon className={`h-3.5 w-3.5 ${cfg.text}`} />
        <span className={`text-[10px] font-bold uppercase tracking-wide ${cfg.text}`}>{cfg.tag}</span>
        <span className="text-[10px] font-mono text-gray-400">· {decision.taxonomyCode}</span>
      </div>
      <p className="text-sm font-semibold text-gray-900">{decision.title}</p>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// Sidebar item
// ═══════════════════════════════════════════════════════════
const SidebarItem = ({ decision, expanded, onSelect, onResolve, onUndo, pending, refCb }) => {
  const cfg = STATUS[decision.uiStatus] || STATUS.current;

  return (
    <div ref={refCb} className={`border-l-4 ${cfg.leftBorder} ${expanded ? 'bg-blue-50/50' : ''}`}>
      <button onClick={onSelect} className="w-full text-left p-3 hover:bg-gray-50">
        <div className="flex items-start gap-2">
          <span className={`mt-1.5 w-2.5 h-2.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs font-mono text-gray-400">{decision.taxonomyCode}</span>
              {decision.decisionType === 'insertion' && (
                <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded">INSERT</span>
              )}
              {decision.wasResolvedByHuman && (
                <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded">Resolved</span>
              )}
            </div>
            <p className="text-sm font-medium text-gray-900 line-clamp-2 mt-0.5">{decision.title}</p>
            <p className="text-xs text-gray-500 capitalize mt-0.5">{(decision.category || '').replace(/_/g, ' ')}</p>
          </div>
          {expanded ? <ChevronUp className="h-4 w-4 mt-1 text-gray-400" /> : <ChevronDown className="h-4 w-4 mt-1 text-gray-400" />}
        </div>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}
            className="overflow-hidden bg-white border-t border-gray-100">
            <ExpandedDetail decision={decision} onResolve={onResolve} onUndo={onUndo} pending={pending} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// Expanded detail with resolution UI
// ═══════════════════════════════════════════════════════════
const ExpandedDetail = ({ decision, onResolve, onUndo, pending }) => {
  const [customMode, setCustomMode] = useState(false);
  const [customText, setCustomText] = useState('');
  const [numberValue, setNumberValue] = useState('');
  const [pickedId, setPickedId] = useState(null);

  const isResolvedByHuman = decision.wasResolvedByHuman;
  const isAuto = decision.uiStatus === 'auto_applied';
  const isResolved = decision.uiStatus === 'current';
  const needsResolution = decision.uiStatus === 'needs_input';

  const options = Array.isArray(decision.optionsV2) ? decision.optionsV2 : [];

  const handlePickOption = (opt) => {
    if (opt.is_custom) {
      if (decision.inputType === 'number_range') {
        // Number input mode handled below
        return;
      }
      setCustomMode(true);
      return;
    }
    setPickedId(opt.id);
    onResolve({
      optionId: opt.id,
      resolutionText: opt.replacement_text || '',
      custom: false
    });
  };

  const handleSubmitCustom = () => {
    if (!customText.trim()) return;
    onResolve({ optionId: 'custom', resolutionText: customText.trim(), custom: true });
  };

  const handleSubmitNumber = () => {
    const validation = validateNumberInput(numberValue, decision.inputConfig);
    if (!validation.valid) {
      alert(validation.error);
      return;
    }
    const finalText = applyValueToTemplate(decision.template, numberValue);
    onResolve({
      optionId: 'custom',
      resolutionText: finalText,
      resolutionValue: numberValue,
      custom: true
    });
  };

  return (
    <div className="p-4 space-y-3 text-sm">

      {decision.plainEnglish && (
        <div className="flex gap-2">
          <Sparkles className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-gray-700">{decision.plainEnglish}</p>
        </div>
      )}

      <p className="text-gray-700 whitespace-pre-wrap">{decision.description}</p>

      {decision.flaggedPhrase && (
        <div className="bg-gray-50 border-l-2 border-gray-300 p-2 rounded text-xs">
          <p className="text-gray-500 uppercase font-semibold mb-1">Current text</p>
          <p className="text-gray-700 italic">"{decision.flaggedPhrase}"</p>
        </div>
      )}

      {decision.insertionPoint && (
        <div className="bg-gray-50 border-l-2 border-gray-300 p-2 rounded text-xs">
          <p className="text-gray-500 uppercase font-semibold mb-1">Insert location</p>
          {decision.insertionPoint.after_heading && (
            <p className="text-gray-700">After heading: "{decision.insertionPoint.after_heading}"</p>
          )}
          {decision.insertionPoint.after_quote && (
            <p className="text-gray-700">After: "{decision.insertionPoint.after_quote.slice(0, 100)}…"</p>
          )}
        </div>
      )}

      <div>
        <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Citation</p>
        <p className="text-xs font-mono text-gray-700">{decision.citation}</p>
      </div>

      {/* Resolved by human */}
      {isResolvedByHuman && (
        <div className="bg-green-50 border border-green-200 rounded p-3">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <p className="text-xs text-green-800 uppercase font-semibold mb-1 flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> Resolved
              </p>
              {decision.resolutionChoice && (
                <p className="text-xs text-gray-600">Option: {decision.resolutionChoice}{decision.customInput ? ' (custom)' : ''}</p>
              )}
            </div>
            <button onClick={onUndo} disabled={pending}
              className="text-xs text-gray-600 hover:text-red-700 flex items-center gap-1 disabled:opacity-50">
              <RotateCcw className="h-3 w-3" /> Undo
            </button>
          </div>
          {decision.resolutionText && (
            <p className="text-xs text-gray-700 whitespace-pre-wrap italic">{decision.resolutionText}</p>
          )}
        </div>
      )}

      {/* Auto-applied */}
      {isAuto && !isResolvedByHuman && (
        <div className="bg-blue-50 border border-blue-200 rounded p-3">
          <p className="text-xs text-blue-800 uppercase font-semibold mb-1 flex items-center gap-1">
            <Info className="h-3 w-3" /> Auto-applied language
          </p>
          {decision.resolutionText && (
            <p className="text-xs text-gray-700 whitespace-pre-wrap italic">{decision.resolutionText}</p>
          )}
        </div>
      )}

      {/* Resolution UI for needs_input / critical */}
      {needsResolution && !isResolvedByHuman && (
        <div className="border-t border-gray-100 pt-3">
          <p className="text-xs text-gray-500 uppercase font-semibold mb-2">
            {decision.inputType === 'number_range' ? 'Enter value'
              : decision.inputType === 'free_text' ? 'Enter text'
              : 'Choose an option'}
          </p>

          {/* number_range input */}
          {decision.inputType === 'number_range' ? (
            <NumberRangeInput
              decision={decision}
              numberValue={numberValue}
              setNumberValue={setNumberValue}
              onSubmit={handleSubmitNumber}
              pending={pending} />
          ) : decision.inputType === 'free_text' ? (
            <div className="space-y-2">
              <textarea value={customText} onChange={e => setCustomText(e.target.value)} rows={4}
                placeholder="Enter the text for your ACOP..."
                className="w-full border border-gray-300 rounded p-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              <button onClick={handleSubmitCustom} disabled={!customText.trim() || pending}
                className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm disabled:opacity-50 hover:bg-blue-700">
                {pending ? 'Saving...' : 'Apply'}
              </button>
            </div>
          ) : (
            <>
              {/* select buttons */}
              <div className="space-y-2">
                {options.map(opt => (
                  <button key={opt.id} onClick={() => handlePickOption(opt)} disabled={pending}
                    className={`w-full text-left border rounded p-2 transition-colors ${
                      pickedId === opt.id ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:bg-gray-50'
                    } ${pending ? 'opacity-50 cursor-wait' : ''}`}>
                    <div className="flex items-start gap-2">
                      <span className="font-mono text-xs text-gray-400 mt-0.5">{opt.id}</span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900">{opt.label}</p>
                        {opt.description && <p className="text-xs text-gray-500 mt-0.5">{opt.description}</p>}
                        {!opt.is_custom && opt.replacement_text && (
                          <p className="text-xs text-gray-600 mt-1 italic whitespace-pre-wrap" style={{ fontFamily: 'Georgia, serif' }}>
                            "{opt.replacement_text}"
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {customMode && (
                <div className="mt-3 space-y-2">
                  <textarea value={customText} onChange={e => setCustomText(e.target.value)} rows={5}
                    placeholder="Enter your custom replacement text..."
                    className="w-full border border-gray-300 rounded p-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                  <div className="flex gap-2">
                    <button onClick={handleSubmitCustom} disabled={!customText.trim() || pending}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm disabled:opacity-50 hover:bg-blue-700">
                      {pending ? 'Saving...' : 'Submit'}
                    </button>
                    <button onClick={() => { setCustomMode(false); setCustomText(''); }}
                      className="px-3 py-1.5 text-gray-600 text-sm hover:text-gray-900">Cancel</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {decision.suggestedAction && (
        <div className="text-xs text-gray-500 italic border-t border-gray-100 pt-2">
          {decision.suggestedAction}
        </div>
      )}

      {decision.reasoning && (
        <details>
          <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">View reasoning</summary>
          <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap">{decision.reasoning}</p>
        </details>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// Number-range input widget
// ═══════════════════════════════════════════════════════════
const NumberRangeInput = ({ decision, numberValue, setNumberValue, onSubmit, pending }) => {
  const cfg = decision.inputConfig || {};
  const preview = numberValue
    ? applyValueToTemplate(decision.template || '', numberValue)
    : decision.template || '';

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {cfg.unit === 'dollars' && <span className="text-gray-500">$</span>}
        <input type="number" value={numberValue}
          onChange={e => setNumberValue(e.target.value)}
          min={cfg.min ?? 0} max={cfg.max}
          step={cfg.value_type === 'integer' ? 1 : 'any'}
          placeholder={cfg.placeholder || '0'}
          className="border border-gray-300 rounded px-3 py-1.5 text-sm w-32 focus:ring-2 focus:ring-blue-500 outline-none" />
        {cfg.unit && cfg.unit !== 'dollars' && <span className="text-gray-500 text-sm">{cfg.unit}</span>}
      </div>
      {(cfg.min != null || cfg.max != null) && (
        <p className="text-xs text-gray-500">
          {cfg.min != null && cfg.max != null ? `Range: ${cfg.min}–${cfg.max}` :
           cfg.min != null ? `Minimum: ${cfg.min}` : `Maximum: ${cfg.max}`}
        </p>
      )}
      {preview && numberValue && (
        <div className="bg-gray-50 border border-gray-200 rounded p-2">
          <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Preview</p>
          <p className="text-xs text-gray-700 italic">"{preview}"</p>
        </div>
      )}
      <button onClick={onSubmit} disabled={!numberValue || pending}
        className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm disabled:opacity-50 hover:bg-blue-700">
        {pending ? 'Saving...' : 'Apply'}
      </button>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// Gap decisions (no section)
// ═══════════════════════════════════════════════════════════
const GapDecisions = ({ decisions, expandedId, onFocus, markRefCb }) => (
  <div className="mt-8 pt-6 border-t-2 border-dashed border-gray-300">
    <h3 className="text-base font-bold text-gray-900 mb-2">Coverage gaps</h3>
    <p className="text-xs text-gray-500 mb-3" style={{ fontFamily: 'system-ui, sans-serif' }}>
      Items not addressed anywhere in your current ACOP — new language can be inserted.
    </p>
    {decisions.map(d => (
      <UnmatchedCard key={d.id} decision={d}
        focused={expandedId === d.id}
        onClick={() => onFocus(d.id)}
        refCb={(el) => markRefCb(d.id, el)} />
    ))}
  </div>
);

export default AcopMonitor;